/**
 * AccessLog — read-only SDB access history.
 *
 * v3 digitization of the paper `SDB Access Tickets - Printable.pdf`.
 * Reads from SessionEvents (eventType in `sdb-access-*`).
 *
 * For Wave 2 we render a static fixture so the surface is visible. Wave 3
 * wires to `apiClient.retail.events.list({ category: 'compliance', ... })` with
 * appropriate server-side filters.
 */
import { useMemo } from 'react';
import { DataGrid } from '@/components/primitives';
import type { DataGridColumn } from '@/components/primitives';
import { formatLocalDateTime } from '@/utils/time';
import type { SdbAccessEvent } from './types';

const DEV_ACCESS_LOG: ReadonlyArray<SdbAccessEvent> = import.meta.env.DEV
  ? [
      {
        eventId: 'EVT-001',
        acctId: 'SDB-0101',
        accessedBy: 'Brown, Mary (CIF-1011)',
        accessedAt: '2026-04-15T14:32:00Z',
        officerCode: 'FSR-042',
        notes: 'Customer retrieved documents.',
      },
      {
        eventId: 'EVT-002',
        acctId: 'SDB-0575',
        accessedBy: 'Garcia, Luis (CIF-2077)',
        accessedAt: '2026-04-12T10:07:00Z',
        officerCode: 'FSR-017',
      },
    ]
  : [];

export function AccessLog(): JSX.Element {
  const rows = useMemo(() => DEV_ACCESS_LOG, []);

  const columns: DataGridColumn<SdbAccessEvent>[] = [
    { key: 'box', label: 'Box', render: (r) => r.acctId },
    { key: 'by', label: 'Accessed by', render: (r) => r.accessedBy },
    {
      key: 'at',
      label: 'When',
      render: (r) => formatLocalDateTime(r.accessedAt),
    },
    { key: 'officer', label: 'Officer', render: (r) => r.officerCode },
    { key: 'notes', label: 'Notes', render: (r) => r.notes ?? '—' },
  ];

  return (
    <div>
      <DataGrid<SdbAccessEvent>
        columns={columns}
        rows={[...rows]}
        rowKey={(r) => r.eventId}
        emptyMessage="No SDB access events found in the selected window."
      />
    </div>
  );
}
